/** Settings read from the <script> tag that loads bc.js. */
export interface ScriptConfig {
  /** Public key of the website, from data-key. */
  publicKey: string;
  /** "auto" blocks known trackers automatically; "manual" only blocks tags marked by the site owner. */
  blockingMode: "auto" | "manual";
  /** Base URL of the Consent API, from data-api (defaults to the production endpoint). */
  apiBaseUrl: string;
  /** data-consentmode="off" stops the Google Consent Mode signals (for sites that manage them elsewhere). */
  googleConsentMode: boolean;
  /**
   * False with data-consentmode="update": the defaults come from elsewhere (the BerryCookie template in
   * Google Tag Manager sets them), and bc.js only sends updates.
   */
  consentDefaults: boolean;
  /** data-country="DE": pretend to be in that country; honoured only by a development Consent API. */
  countryOverride: string | null;
  /** data-tcf="true": install the IAB TCF stub at once, before the banner configuration arrives. */
  tcf: boolean;
  /** Where bc.js was loaded from; bc-tcf.js is loaded from the same place. */
  scriptUrl: string | null;
}

export const DEFAULT_API_BASE_URL = "https://consent.berrycookie.com/v1";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A setting from a data- attribute, or from the same-named query parameter of the script URL
 * (bc.js?key=…), for loaders that cannot set attributes, such as Google Tag Manager templates.
 */
function setting(script: HTMLScriptElement, name: string): string | null {
  const attribute = script.getAttribute(`data-${name}`);
  if (attribute !== null) {
    return attribute;
  }
  const src = script.getAttribute("src");
  if (!src) {
    return null;
  }
  try {
    return new URL(src, "https://x.invalid/").searchParams.get(name);
  } catch {
    return null;
  }
}

/**
 * Reads the configuration from the script element. Returns null when the tag is missing
 * or the public key is invalid, so the SDK can stay inactive instead of breaking the page.
 */
export function readScriptConfig(
  script: HTMLScriptElement | null,
): ScriptConfig | null {
  if (!script) {
    return null;
  }

  const publicKey = setting(script, "key")?.trim() ?? "";
  if (!UUID_PATTERN.test(publicKey)) {
    return null;
  }

  const mode = setting(script, "blockingmode");
  const api = setting(script, "api")?.trim();
  const consentMode = setting(script, "consentmode");
  const country = setting(script, "country")?.trim().toUpperCase() ?? "";

  return {
    publicKey: publicKey.toLowerCase(),
    blockingMode: mode === "manual" ? "manual" : "auto",
    apiBaseUrl: api ? api.replace(/\/+$/, "") : DEFAULT_API_BASE_URL,
    googleConsentMode: consentMode !== "off",
    consentDefaults: consentMode !== "off" && consentMode !== "update",
    countryOverride: /^[A-Z]{2}$/.test(country) ? country : null,
    tcf: ["true", "1"].indexOf(setting(script, "tcf") ?? "") >= 0,
    scriptUrl: script.src || null,
  };
}

/** Finds the tag that loaded the SDK: the recommended id first, then the current script. */
export function findSdkScript(doc: Document): HTMLScriptElement | null {
  const byId = doc.getElementById("BerryCookie");
  if (byId instanceof HTMLScriptElement) {
    return byId;
  }
  return doc.currentScript instanceof HTMLScriptElement
    ? doc.currentScript
    : null;
}
